/**
 * THE payload builder for the `enrichReference` callable. Every caller — the
 * single-ref editor, bulk AI Enrich, bulk import, Description Regen and the bulk
 * description rewrite — builds `referenceData` here, in the STORED-REF shape.
 *
 * One shape is what lets the callable enforce its allowlist
 * (functions/src/enrichReferenceSections.ts): it decides what to send by field
 * NAME, so every caller has to use the same names. Bulk import used to send the
 * raw scraped entry (`caseMat`, `powerReserve`, a top-level `price`) and would
 * have lost those fields to it; it now maps the entry to a reference first.
 *
 * This module removes what must never reach a model whatever the prompt says —
 * pricing (Watch Keeper is a catalogue, not a pricing app), media, and internal
 * bookkeeping. WHICH spec fields are worth sending is the server's call, made
 * against the prompt actually in use; a second list here would drift from it.
 */
import type { SiblingReference } from './notableVariantCandidates';

/**
 * Keys never sent. The union of what the full-enrich and description builders
 * each excluded before they shared this one.
 */
const NEVER_SEND = new Set<string>([
  // Internal / bookkeeping
  'id',
  'brandId',
  'createdAt',
  'updatedAt',
  'searchText',
  'referenceNormalized',
  'parentReferenceId',
  'links',
  'source',
  'aggregateOverrides',
  'descriptionSource',
  'descriptionAiRewrittenAt',
  // Media — images go to the callable as tagged image blocks, never as text
  'pendingImageUrl',
  'imageUrl',
  'imagePath',
  'thumbnailUrl',
  'images',
  'galleryImages',
  // Pricing — never, in any form
  'retailPrice',
  'price',
  'priceRaw',
  'salePrice',
  'compareAtPrice',
  'msrp',
  'currency',
]);

export interface EnrichPayloadOptions {
  brandName: string;
  /**
   * `WatchBrand.founded` — the prompt's floor for `releaseYear`, and the
   * callable's, which drops any year before it. Never `yearRevived`: a revived
   * brand's vintage pieces predate the revival. Sent as `brandFounded`.
   */
  brandFounded?: number | null;
  /**
   * Real catalogue siblings — the only references the prompt may list as
   * variants. Same-collection, each with its model and nickname where known
   * (see `notableVariantCandidates.ts`).
   */
  siblingReferences?: readonly SiblingReference[];
  /** Further keys a particular caller must not send. */
  omit?: Iterable<string>;
  /**
   * Send only top-level strings and numbers. The description tools write from
   * the flat spec fields and have always skipped nested objects.
   */
  primitivesOnly?: boolean;
}

export function buildEnrichReferencePayload(
  ref: Readonly<Record<string, unknown>>,
  { brandName, brandFounded, siblingReferences = [], omit = [], primitivesOnly = false }: EnrichPayloadOptions,
): Record<string, unknown> {
  const skip = new Set(omit);
  const payload: Record<string, unknown> = { brand: brandName };
  if (typeof brandFounded === 'number' && Number.isInteger(brandFounded)) payload.brandFounded = brandFounded;
  for (const [key, value] of Object.entries(ref)) {
    if (NEVER_SEND.has(key) || skip.has(key)) continue;
    if (key === 'brand' || key === 'brandFounded' || key === 'siblingReferences') continue; // set from options
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    if (primitivesOnly && typeof value !== 'string' && typeof value !== 'number') continue;
    payload[key] = value;
  }
  if (siblingReferences.length > 0) {
    payload.siblingReferences = siblingReferences.map((sibling) => ({ ...sibling }));
  }
  return payload;
}
